import {
    Container,
    ContainerItem,
    DeliveryTime,
    DeliveryType,
    DeliveryZone,
    Item,
    Order,
    OrderActivityActor,
    OrderActivityLog,
    OrderActivityLogPayload,
    PaymentType,
    Price,
    RemovedIngredient,
    Topping,
} from '@/features/orders/types/app'
import {
    Container as FirebaseContainer,
    ContainerItem as FirebaseContainerItem,
    DeliveryTime as FirebaseDeliveryTime,
    DeliveryType as FirebaseDeliveryType,
    DeliveryZone as FirebaseDeliveryZone,
    Item as FirebaseItem,
    Order as FirebaseOrder,
    OrderActivityActor as FirebaseOrderActivityActor,
    OrderActivityLog as FirebaseOrderActivityLog,
    OrderActivityLogPayload as FirebaseOrderActivityLogPayload,
    PaymentType as FirebasePaymentType,
    Price as FirebasePrice,
    RemovedIngredient as FirebaseRemovedIngredient,
    Topping as FirebaseTopping,
} from '@/features/orders/types/firebase'
import {
    convertChannelToApp,
    convertDeliveryTimeTypeToApp,
    convertDeliveryTypeCategoryToApp,
    convertPaymentTypeToApp,
    convertPhaseToApp,
    convertStateToApp,
} from '@/features/orders/utils/conversions'

const transformDeliveryTime = (deliveryTime: FirebaseDeliveryTime): DeliveryTime => ({
    type: convertDeliveryTimeTypeToApp(deliveryTime.type),
    start: deliveryTime.start,
    end: deliveryTime.end,
})

const transformDeliveryType = (deliveryType: FirebaseDeliveryType): DeliveryType => ({
    typeCategory: convertDeliveryTypeCategoryToApp(deliveryType.typeCategory),
})

const transformDeliveryZone = (deliveryZone: FirebaseDeliveryZone): DeliveryZone => ({
    id: deliveryZone.uuid,
    name: deliveryZone.name,
    color: deliveryZone.color,
})

const transformPaymentType = (paymentType: FirebasePaymentType): PaymentType => ({
    type: convertPaymentTypeToApp(paymentType.type),
})

const transformContainer = (container: FirebaseContainer): Container => ({
    id: container.uuid,
    name: container.name,
    price: container.price,
})

const transformContainerItem = (containerItem: FirebaseContainerItem): ContainerItem => ({
    id: containerItem.uuid,
    name: containerItem.name,
    price: containerItem.price,
    quantity: containerItem.quantity,
})

const transformRemovedIngredient = (
    removedIngredient: FirebaseRemovedIngredient
): RemovedIngredient => ({
    id: removedIngredient.uuid,
    name: removedIngredient.name,
})

const transformTopping = (topping: FirebaseTopping): Topping => ({
    id: topping.uuid,
    name: topping.name,
    quantity: topping.quantity,
    price: topping.unitPrice,
})

const transformPrice = (price: FirebasePrice): Price => ({
    amount: price.amount,
    currency: price.currency,
})

const transformOrderActivityLogType = (
    activityType: FirebaseOrderActivityLog['type']
): OrderActivityLog['type'] => activityType

const transformOrderActivityActorType = (
    actorType: FirebaseOrderActivityActor['type']
): OrderActivityActor['type'] => actorType

const transformOrderActivityActor = (
    actor: FirebaseOrderActivityActor | null
): OrderActivityActor | null => {
    if (!actor) return null

    return {
        description: actor.description ?? null,
        id: actor.id ?? null,
        label: actor.label ?? null,
        type: transformOrderActivityActorType(actor.type ?? null),
    }
}

const transformOrderActivityLogPayload = (
    payload: FirebaseOrderActivityLogPayload = {}
): OrderActivityLogPayload => ({
    ...payload,
    deliveryTimeShiftMinutes: payload.deliveryTimeShiftMinutes,
    paymentType: payload.paymentType,
})

const transformOrderActivityLog = (activityLog: FirebaseOrderActivityLog): OrderActivityLog => ({
    actor: transformOrderActivityActor(activityLog.actor),
    occurredAt: activityLog.occurredAt,
    payload: transformOrderActivityLogPayload(activityLog.payload),
    type: transformOrderActivityLogType(activityLog.type),
})

const transformItem = (item: FirebaseItem): Item => ({
    id: item.uuid,
    addedItems: item.addedItems?.map(transformItem),
    container: item.container && transformContainer(item.container),
    name: item.name,
    quantity: item.quantity,
    removedIngredients: item.removedIngredients?.map(transformRemovedIngredient),
    toppings: item.toppings?.map(transformTopping),
    price: transformPrice(item.unitPrice),
})

export const transformOrder = (order: FirebaseOrder): Order => ({
    id: order.uuid,
    acceptedDeliveryTime:
        order.acceptedDeliveryTime && transformDeliveryTime(order.acceptedDeliveryTime),
    activityLogs: order.activityLogs?.map(transformOrderActivityLog),
    channel: convertChannelToApp(order.channel),
    containerItems: order.containerItems?.map(transformContainerItem) ?? [],
    createdAt: order.createdAt,
    currency: order.currency,
    customerName: order.customerName,
    customerNote: order.customerNote,
    deliveredAt: order.deliveredAt,
    deliveryAddress: order.deliveryAddress,
    deliveryPrice: order.deliveryPrice && transformPrice(order.deliveryPrice),
    deliveryType: transformDeliveryType(order.deliveryType),
    deliveryZone: order.deliveryZone && transformDeliveryZone(order.deliveryZone),
    items: order.items?.map(transformItem) ?? [],
    paymentType: order.paymentType && transformPaymentType(order.paymentType),
    phase: convertPhaseToApp(order.phase),
    phoneNumber: order.phoneNumber,
    price: transformPrice(order.price),
    postponedUntil: order.postponedUntil,
    receiptUuid: order.receiptUuid ?? null,
    requestedDeliveryTime:
        order.requestedDeliveryTime && transformDeliveryTime(order.requestedDeliveryTime),
    shortCode: order.shortCode,
    smallOrderSurcharge: order.smallOrderSurcharge && transformPrice(order.smallOrderSurcharge),
    state: convertStateToApp(order.state),
    tip: order.tip && transformPrice(order.tip),
})
